import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble";
import {
  resolveTranscriptSpeakerCurrentUserContext,
  resolveTranscriptSpeakerParticipantDisplayLabel,
  resolveTranscriptSpeakerPresentation,
  type TranscriptSpeakerCurrentUser,
} from "@/modules/audio-recording/transcript-speaker-identity";
import { resolveTranscriptSpeakerBadgeName } from "@/modules/audio-recording/transcript-speaker-badges";
import { normalizeSpeakerParticipantName } from "@/modules/audio-recording/transcript-speaker-labels";
import type {
  TranscriptArrayItem,
  TranscriptArraySpeakerParticipant,
  TranscriptSpeakerParticipantMetadata,
} from "@/modules/audio-recording/transcript-types";

export type FloatingAudioPreviewTranscriptBubble = {
  key: string;
  text: string;
  alignment: MDSBubbleAlignment;
  variant: MDSBubbleVariant;
  /** Stable visual grouping identity used to decide when speaker badges repeat. */
  speakerGroupKey?: string;
  speakerDisplayName?: string;
  isLastInGroup: boolean;
};

/**
 * Converts persisted transcript array items into the same speaker-aware bubble
 * model used during live recording, preserving participant labels in the opened
 * Voice Recording attachment instead of falling back to channel-only display.
 */
export function buildFloatingAudioPreviewTranscriptBubbles({
  transcriptEntries,
  currentUser = null,
  prefixSelfSpeaker = false,
  prefixSpeakerDisplayNames = false,
}: {
  transcriptEntries: TranscriptArrayItem[];
  currentUser?: TranscriptSpeakerCurrentUser | null;
  prefixSelfSpeaker?: boolean;
  prefixSpeakerDisplayNames?: boolean;
}): FloatingAudioPreviewTranscriptBubble[] {
  const entriesWithParticipants = transcriptEntries.map(entry => ({
    entry,
    speakerParticipant: normalizeTranscriptArraySpeakerParticipant(entry.speaker_participant),
  }));
  const currentUserContext = resolveTranscriptSpeakerCurrentUserContext({
    currentUser,
    speakers: entriesWithParticipants.map(({ entry, speakerParticipant }) => ({
      speakerId: entry.speaker_id,
      speakerParticipant,
    })),
  });

  return entriesWithParticipants.map(({ entry, speakerParticipant }, index, entries) => {
    const speakerPresentation = resolveTranscriptSpeakerPresentation({
      speakerVariant: entry.speaker_variant,
      channel: entry.channel,
      speakerParticipant,
      speakerId: entry.speaker_id,
      currentUser: currentUserContext,
    });
    const speakerDisplayName = resolveTranscriptSpeakerBadgeName({
      isSelfSpeaker: speakerPresentation.isSelfSpeaker,
      isTranscriptOnly: speakerPresentation.isTranscriptOnly,
      channel: entry.channel,
      speakerParticipant,
      speakerLabel: entry.speaker_label,
      speakerLabelKind: entry.speaker_label_kind,
    });
    const nextEntry = entries[index + 1];
    const nextVisualGroupKey = nextEntry
      ? resolvePreviewTranscriptVisualGroupKey({
          entry: nextEntry.entry,
          speakerParticipant: nextEntry.speakerParticipant,
          currentUser: currentUserContext,
        })
      : null;
    const participantDisplayLabel = resolveTranscriptSpeakerParticipantDisplayLabel({
      speakerParticipant,
      includeEmail: true,
    });
    const speakerAttributionPrefix =
      !speakerPresentation.isSelfSpeaker &&
      !speakerPresentation.isTranscriptOnly &&
      !speakerDisplayName
        ? buildSpeakerAttributionPrefix(speakerParticipant)
        : null;
    const shouldPrefix =
      prefixSpeakerDisplayNames &&
      participantDisplayLabel &&
      !speakerPresentation.isTranscriptOnly &&
      (speakerPresentation.isSelfSpeaker ? prefixSelfSpeaker : true);
    const transcript = entry.transcript.trim();
    const text = shouldPrefix ? `[${participantDisplayLabel}] ${transcript}` : transcript;

    return {
      key: entry.sort_key,
      text: speakerAttributionPrefix ? `${speakerAttributionPrefix} ${text}` : text,
      alignment: speakerPresentation.isTranscriptOnly
        ? MDSBubbleAlignment.Center
        : speakerPresentation.isSelfSpeaker
          ? MDSBubbleAlignment.Right
          : MDSBubbleAlignment.Left,
      variant: speakerPresentation.isTranscriptOnly
        ? MDSBubbleVariant.Transparent
        : speakerPresentation.isSelfSpeaker
          ? MDSBubbleVariant.VoiceSelf
          : MDSBubbleVariant.VoiceOthers,
      speakerGroupKey: speakerPresentation.groupKey,
      ...(speakerDisplayName ? { speakerDisplayName } : {}),
      isLastInGroup: speakerPresentation.groupKey !== nextVisualGroupKey,
    };
  });
}

function resolvePreviewTranscriptVisualGroupKey({
  entry,
  speakerParticipant,
  currentUser,
}: {
  entry: TranscriptArrayItem;
  speakerParticipant: TranscriptSpeakerParticipantMetadata | null;
  currentUser: TranscriptSpeakerCurrentUser | null;
}): string {
  const speakerPresentation = resolveTranscriptSpeakerPresentation({
    speakerVariant: entry.speaker_variant,
    channel: entry.channel,
    speakerParticipant,
    speakerId: entry.speaker_id,
    currentUser,
  });

  return speakerPresentation.groupKey;
}

function buildSpeakerAttributionPrefix(
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null
): string | null {
  const speakerName = normalizeSpeakerParticipantName(speakerParticipant?.name);
  const speakerEmail = normalizeSpeakerParticipantName(speakerParticipant?.email);
  const speakerAttribution = [speakerName, speakerEmail].filter(Boolean).join(" - ");

  return speakerAttribution ? `[${speakerAttribution}]` : null;
}

function normalizeTranscriptArraySpeakerParticipant(
  participant: TranscriptArraySpeakerParticipant | null | undefined
): TranscriptSpeakerParticipantMetadata | null {
  if (!participant) return null;

  return {
    id: participant.id,
    name: participant.name,
    isPlatformHost: participant.is_platform_host,
    isHost: participant.is_host,
    platform: participant.platform,
    email: participant.email,
    extraData: participant.extra_data,
  };
}
